import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Play,
  Music,
  MessageSquare,
  Sparkles,
  Wand2,
  Languages,
  Rocket,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { getFileUrl } from "@/lib/utils";
import { getPaginationItems, PAGINATION_DOTS } from "@/lib/pagination";
import type { PaginationItem } from "@/lib/pagination";
import { isMobile as detectMobile } from "@/lib/is-mobile";
import { useRunHistory } from "../hooks/use-run-history";
import { ImageViewer } from "@/components/ui/image-viewer";
import { VideoViewer } from "@/components/ui/video-viewer";
import { AudioViewer } from "@/components/ui/audio-viewer";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import type { MessageInputResponse } from "@/api/hooks/use-get-message-input";
import { useToast } from "@/features/toast/hooks/use-toast";
import { modelsPaths } from "@/modules";

type RunStatus = "success" | "error";
type SortField = "status";

interface RunHistoryRecord {
  id: string;
  modelEndpoint: string;
  status: RunStatus;
  runTime: number;
  tokenAmount: number;
  outputUrl: string | null;
  fileType: "image" | "video" | "audio";
  formattedDate: string;
  createdAt: string;
  messageId: string | null;
  isClickable: boolean;
  modelId: string | null;
  conversationId: string | null;
  type:
    | "generation"
    | "chat"
    | "enhancement"
    | "translation"
    | "onboarding"
    | null;
  libraryItems: Array<{
    _id: string;
    title: string;
    type: string;
    file: any;
  }> | null;
}

export const RunHistorySection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { error: errorToast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [clickingRecordId, setClickingRecordId] = useState<string | null>(null);

  // Carousel states - track current index for each record
  const [carouselIndexes, setCarouselIndexes] = useState<
    Record<string, number>
  >({});

  // Viewer states
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    title: string;
    id: string;
  } | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<{
    url: string;
    title: string;
    id: string;
  } | null>(null);
  const [selectedAudio, setSelectedAudio] = useState<{
    url: string;
    title: string;
    id: string;
  } | null>(null);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: 10,
      sortBy: "-createdAt",
    }),
    [currentPage]
  );

  const {
    runHistory,
    totalPages = 1,
    isRunHistoryLoading,
    runHistoryError,
  } = useRunHistory(queryParams);

  const formattedRunHistory: RunHistoryRecord[] = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    });

    return runHistory.map((record) => {
      const normalizedStatus: RunStatus =
        record.status?.toLowerCase() === "success" ? "success" : "error";

      // Get current carousel index for this record (default to 0)
      const currentIndex = carouselIndexes[record._id] || 0;
      // Get current library item for output display
      const currentLibraryItem = record.libraryItemIds?.[currentIndex];
      const outputUrl = currentLibraryItem?.file
        ? getFileUrl(currentLibraryItem.file)
        : null;

      // Determine file type from current library item
      let fileType: "image" | "video" | "audio" = "image"; // default

      if (currentLibraryItem) {
        const libraryType = currentLibraryItem.type?.toLowerCase() ?? "";

        // First check library item type
        if (libraryType === "audio") {
          fileType = "audio";
        } else if (libraryType === "video") {
          fileType = "video";
        } else if (libraryType === "image") {
          fileType = "image";
        } else if (currentLibraryItem.file?.type) {
          // Check MIME type
          const mimeType = currentLibraryItem.file.type.toLowerCase();
          if (mimeType.startsWith("audio/")) {
            fileType = "audio";
          } else if (mimeType.startsWith("video/")) {
            fileType = "video";
          } else if (mimeType.startsWith("image/")) {
            fileType = "image";
          }
        } else if (currentLibraryItem.file?.fileUrl) {
          // Fallback: check file extension
          const extension = currentLibraryItem.file.fileUrl
            .split(".")
            .pop()
            ?.toLowerCase();
          const audioExtensions = ["mp3", "wav", "m4a", "aac", "flac", "oga"];
          const videoExtensions = [
            "mp4",
            "avi",
            "mov",
            "wmv",
            "flv",
            "webm",
            "mkv",
          ];

          if (audioExtensions.includes(extension || "")) {
            fileType = "audio";
          } else if (videoExtensions.includes(extension || "")) {
            fileType = "video";
          }
        }
      }

      // Check if record is clickable (messageId and libraryItemIds must exist)
      const isClickable = !!(
        record.messageId &&
        record.libraryItemIds &&
        record.libraryItemIds.length > 0
      );

      return {
        id: record._id,
        modelEndpoint:
          record.modelId?.title ?? t("error.unknownModel", "Unknown model"),
        status: normalizedStatus,
        runTime: typeof record.runTime === "number" ? record.runTime : 0,
        tokenAmount:
          typeof record.tokenAmount === "number" ? record.tokenAmount : 0,
        outputUrl,
        fileType,
        formattedDate: formatter.format(new Date(record.createdAt)),
        createdAt: record.createdAt,
        messageId: record.messageId ?? null,
        isClickable,
        modelId: record.modelId?._id ?? null,
        conversationId: record.conversationId ?? null,
        type: record.type ?? null,
        libraryItems: record.libraryItemIds
          ? record.libraryItemIds.map((item) => ({
              _id: item._id,
              title: item.title,
              type: item.type,
              file: item.file,
            }))
          : null,
      };
    });
  }, [i18n.language, runHistory, t, carouselIndexes]);

  const sortedRunHistory = useMemo(() => {
    if (!sortField) return formattedRunHistory;

    const direction = sortDirection === "asc" ? 1 : -1;
    return [...formattedRunHistory].sort((a, b) => {
      if (sortField === "status") {
        const weight = (status: RunStatus) => (status === "success" ? 0 : 1);
        return (weight(a.status) - weight(b.status)) * direction;
      }

      return 0;
    });
  }, [formattedRunHistory, sortDirection, sortField]);

  const totalPagesCount = Math.max(totalPages, 1);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    );
  };

  const StatusBadge = ({ status }: { status: RunStatus }) => {
    if (status === "success") {
      return (
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <CheckCircle className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
          <span className="text-xs font-medium text-green-500 dark:text-green-400">
            {t("settings.usage.success")}
          </span>
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
        <XCircle className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
        <span className="text-xs font-medium text-red-500 dark:text-red-400">
          {t("settings.usage.error")}
        </span>
      </div>
    );
  };

  // Type icon and background based on type
  const getTypeDisplay = (
    type:
      | "generation"
      | "chat"
      | "enhancement"
      | "translation"
      | "onboarding"
      | null
  ) => {
    if (type === "generation") {
      return {
        icon: Sparkles,
        bgColor: "bg-gradient-to-br from-purple-500 to-pink-500",
        iconColor: "text-white",
      };
    } else if (type === "chat") {
      return {
        icon: MessageSquare,
        bgColor: "bg-blue-500 dark:bg-blue-600",
        iconColor: "text-white",
      };
    } else if (type === "enhancement") {
      return {
        icon: Wand2,
        bgColor: "bg-emerald-500 dark:bg-emerald-600",
        iconColor: "text-white",
      };
    } else if (type === "translation") {
      return {
        icon: Languages,
        bgColor: "bg-amber-500 dark:bg-amber-600",
        iconColor: "text-white",
      };
    } else if (type === "onboarding") {
      return {
        icon: Rocket,
        bgColor: "bg-indigo-500 dark:bg-indigo-600",
        iconColor: "text-white",
      };
    }

    // Default for null or unknown types (generation style)
    return {
      icon: Sparkles,
      bgColor: "bg-gradient-to-br from-purple-500 to-pink-500",
      iconColor: "text-white",
    };
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPagesCount, prev + 1));
  };

  // Carousel navigation handlers
  const handleCarouselPrev = (recordId: string, totalItems: number) => {
    setCarouselIndexes((prev) => {
      const currentIndex = prev[recordId] || 0;
      return {
        ...prev,
        [recordId]: currentIndex > 0 ? currentIndex - 1 : totalItems - 1,
      };
    });
  };

  const handleCarouselNext = (recordId: string, totalItems: number) => {
    setCarouselIndexes((prev) => {
      const currentIndex = prev[recordId] || 0;
      return {
        ...prev,
        [recordId]: currentIndex < totalItems - 1 ? currentIndex + 1 : 0,
      };
    });
  };

  const handleModelClick = async (record: RunHistoryRecord) => {
    if (!record.isClickable || !record.messageId) return;

    try {
      setClickingRecordId(record.id);

      // API'den message input data'yı çek
      const editDataResult = await apiClient.get<MessageInputResponse>(
        API_ENDPOINTS.getMessageInput(record.messageId)
      );

      const editData = editDataResult.data;

      if (!editData || !editData.model || !editData.input) {
        throw new Error(t("error.invalidEditData", "Invalid edit data"));
      }

      // Model ID'yi al
      const modelId = editData.model._id;
      const inputData = editData.input.inputData;

      // Output library'yi bul (response array'inden ilk library item'ı)
      const outputLibrary =
        editData.message?.response?.find(
          (r: any) => r.library && r.library.file
        )?.library || null;

      // Wizard'a navigate et - edit data ile + u parametresi (run history record id)
      const chatIdParam = record.conversationId
        ? `/${record.conversationId}`
        : "";
      navigate(`${modelsPaths.wizard(modelId)}${chatIdParam}?u=${record.id}`, {
        state: {
          editData: {
            inputData,
            outputLibrary,
          },
        },
      });
    } catch (apiError: any) {
      console.error("Model click error:", apiError);
      errorToast(
        apiError?.message ||
          t("error.failedToLoadModelData", "Failed to load model data")
      );
    } finally {
      setClickingRecordId(null);
    }
  };

  const [isMobile, setIsMobile] = useState(detectMobile());

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(detectMobile());
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const paginationItems = useMemo(
    () =>
      getPaginationItems({
        totalPages: totalPagesCount,
        currentPage,
        maxLength: isMobile ? 5 : 9,
      }),
    [currentPage, isMobile, totalPagesCount]
  );

  const noData =
    !sortedRunHistory.length && !isRunHistoryLoading && !runHistoryError;

  const errorMessage =
    runHistoryError instanceof Error
      ? runHistoryError.message
      : runHistoryError
      ? String(runHistoryError)
      : null;

  return (
    <div className="rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-4 flex flex-col gap-4">
      <p className="text-lg font-semibold text-text-primary dark:text-gray-100">
        {t("settings.usage.runHistory")}
      </p>

      {isRunHistoryLoading && (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-text-secondary dark:text-gray-400">
            {t("common.loading")}...
          </span>
        </div>
      )}

      {errorMessage && !isRunHistoryLoading && (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-red-500">
            {t("common.error")}: {errorMessage}
          </span>
        </div>
      )}

      {noData && (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-text-secondary dark:text-gray-400">
            {t("settings.usage.noDataAvailable")}
          </span>
        </div>
      )}

      {!isRunHistoryLoading && !errorMessage && !noData && (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Table Header */}
                <thead className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-border-secondary rounded-xl overflow-hidden">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                          {t("settings.usage.modelEndpoint")}
                        </span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left w-[200px]">
                      <button
                        onClick={() => handleSort("status")}
                        className="flex items-center gap-1 hover:text-text-secondary dark:hover:text-gray-300 transition-colors"
                      >
                        <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                          {t("settings.usage.status")}
                        </span>
                        <SortIcon field="status" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left w-[200px]">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                          {t("settings.usage.runTime")}
                        </span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left w-[200px]">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                          {t("settings.usage.tokenAmount")}
                        </span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left w-[200px]">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                          {t("settings.usage.output")}
                        </span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left w-[200px]">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                          {t("settings.usage.date")}
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-border-secondary dark:divide-gray-600">
                  {sortedRunHistory.map((record) => {
                    return (
                      <tr
                        key={record.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td className="px-6 py-4">
                          {record.isClickable ? (
                            <button
                              onClick={() => handleModelClick(record)}
                              disabled={clickingRecordId === record.id}
                              className="text-sm text-text-tertiary dark:text-gray-300 truncate max-w-[200px] underline hover:text-text-primary dark:hover:text-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                            >
                              {clickingRecordId === record.id
                                ? t("common.loading", "Loading") + "..."
                                : record.modelEndpoint}
                            </button>
                          ) : (
                            <span className="text-sm text-text-tertiary dark:text-gray-300 truncate max-w-[200px]">
                              {record.modelEndpoint}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={record.status} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-text-primary dark:text-gray-100">
                            {record.runTime.toFixed(2)}s
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-text-primary dark:text-gray-100  ">
                            {record.tokenAmount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative inline-flex items-center gap-2">
                          <div
                            className="relative w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              if (record.outputUrl) {
                                if (record.fileType === "video") {
                                  setSelectedVideo({
                                    url: record.outputUrl,
                                    title: record.modelEndpoint,
                                    id: record.id,
                                  });
                                } else if (record.fileType === "audio") {
                                  setSelectedAudio({
                                    url: record.outputUrl,
                                    title: record.modelEndpoint,
                                    id: record.id,
                                  });
                                } else {
                                  setSelectedImage({
                                    url: record.outputUrl,
                                    title: record.modelEndpoint,
                                    id: record.id,
                                  });
                                }
                              }
                            }}
                          >
                              {record.outputUrl &&
                              (record.type === "generation" ||
                                record.type === "onboarding" ||
                                record.type === null) ? (
                              <>
                                {/* Audio Thumbnail */}
                                {record.fileType === "audio" ? (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                                    <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center mb-1">
                                      <Music className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                ) : record.fileType === "video" ? (
                                  /* Video Thumbnail */
                                  <video
                                    src={record.outputUrl}
                                    preload="metadata"
                                    className="w-full h-full object-cover"
                                    onError={(event) => {
                                      const target =
                                        event.target as HTMLVideoElement;
                                      target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  /* Image Thumbnail */
                                  <img
                                    src={record.outputUrl}
                                    alt={record.modelEndpoint}
                                    className="w-full h-full object-cover"
                                    onError={(event) => {
                                      const target =
                                        event.target as HTMLImageElement;
                                      target.style.display = "none";
                                    }}
                                  />
                                )}

                                {/* Video Play Button Overlay */}
                                {record.fileType === "video" && (
                                  <div className="absolute inset-0 bg-black/15 flex items-center justify-center">
                                    <div className="w-6 h-6 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                      <Play className="w-3 h-3 text-white fill-white ml-0.5" />
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                                /* Type-based icon display */
                                (() => {
                                  const typeDisplay = getTypeDisplay(
                                    record.type
                                  );
                                  const Icon = typeDisplay.icon;
                                  return (
                                    <div
                                      className={`w-full h-full flex items-center justify-center ${typeDisplay.bgColor}`}
                                    >
                                <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                        <Icon
                                          className={`w-4 h-4 ${typeDisplay.iconColor}`}
                                        />
                                      </div>
                                    </div>
                                  );
                                })()
                              )}
                            </div>

                            {/* Carousel Controls - Only show if multiple items */}
                            {record.libraryItems &&
                              record.libraryItems.length > 1 && (
                                <div className="flex flex-col gap-1">
                                  {/* Navigation Buttons */}
                                  <div className="flex gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCarouselPrev(
                                          record.id,
                                          record.libraryItems!.length
                                        );
                                      }}
                                      className="p-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                      aria-label="Previous"
                                    >
                                      <ArrowLeft className="w-3 h-3 text-gray-700 dark:text-gray-300" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCarouselNext(
                                          record.id,
                                          record.libraryItems!.length
                                        );
                                      }}
                                      className="p-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                      aria-label="Next"
                                    >
                                      <ArrowRight className="w-3 h-3 text-gray-700 dark:text-gray-300" />
                                    </button>
                                  </div>
                                  {/* Indicator */}
                                  <div className="text-[10px] text-center text-gray-600 dark:text-gray-400 font-medium">
                                    {(carouselIndexes[record.id] || 0) + 1}/
                                    {record.libraryItems.length}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-text-tertiary dark:text-gray-300">
                            {record.formattedDate}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Desktop Pagination */}
            <div className="border-t border-border-secondary dark:border-gray-600 px-6 py-3 flex items-center justify-between">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-3.5 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-sm font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={t("settings.usage.previous")}
              >
                <ChevronLeft className="w-4 h-4" />
                {!isMobile && <span>{t("settings.usage.previous")}</span>}
              </button>

              <div className="flex gap-0.5">
                {paginationItems.map((item: PaginationItem, index) => {
                  if (item === PAGINATION_DOTS) {
                    return (
                      <span
                        key={`desktop-dots-${index}`}
                        className="px-2 text-text-quaternary dark:text-gray-400"
                      >
                        ...
                      </span>
                    );
                  }

                  const page = item as number;

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                        currentPage === page
                          ? "bg-gray-50 dark:bg-gray-700 text-text-secondary dark:text-gray-200"
                          : "text-text-quaternary dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPagesCount}
                className="flex items-center gap-1 px-3.5 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-sm font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={t("settings.usage.next")}
              >
                {!isMobile && <span>{t("settings.usage.next")}</span>}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden flex flex-col gap-3">
            {sortedRunHistory.map((record) => (
              <div
                key={record.id}
                className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl p-3"
              >
                <div className="flex gap-3 items-center">
                  {/* Left: Output Thumbnail */}
                  <div className="flex-shrink-0">
                      <div
                      className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          if (record.outputUrl) {
                            if (record.fileType === "video") {
                              setSelectedVideo({
                                url: record.outputUrl,
                                title: record.modelEndpoint,
                                id: record.id,
                              });
                            } else if (record.fileType === "audio") {
                              setSelectedAudio({
                                url: record.outputUrl,
                                title: record.modelEndpoint,
                                id: record.id,
                              });
                            } else {
                              setSelectedImage({
                                url: record.outputUrl,
                                title: record.modelEndpoint,
                                id: record.id,
                              });
                            }
                          }
                        }}
                      >
                      {record.outputUrl &&
                      (record.type === "generation" ||
                        record.type === "onboarding" ||
                        record.type === null) ? (
                          <>
                            {/* Audio Thumbnail */}
                            {record.fileType === "audio" ? (
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                              <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                <Music className="w-4 h-4 text-white" />
                                </div>
                              </div>
                            ) : record.fileType === "video" ? (
                              /* Video Thumbnail */
                              <video
                                src={record.outputUrl}
                                preload="metadata"
                                className="w-full h-full object-cover"
                                onError={(event) => {
                                const target = event.target as HTMLVideoElement;
                                  target.style.display = "none";
                                }}
                              />
                            ) : (
                              /* Image Thumbnail */
                              <img
                                src={record.outputUrl}
                                alt={record.modelEndpoint}
                                className="w-full h-full object-cover"
                                onError={(event) => {
                                const target = event.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            )}

                            {/* Video Play Button Overlay */}
                            {record.fileType === "video" && (
                              <div className="absolute inset-0 bg-black/15 flex items-center justify-center">
                              <div className="w-6 h-6 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                <Play className="w-3 h-3 text-white fill-white ml-0.5" />
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                        /* Type-based icon display */
                        (() => {
                          const typeDisplay = getTypeDisplay(record.type);
                          const Icon = typeDisplay.icon;
                          return (
                            <div
                              className={`w-full h-full flex items-center justify-center ${typeDisplay.bgColor}`}
                            >
                              <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                <Icon
                                  className={`w-4 h-4 ${typeDisplay.iconColor}`}
                                />
                            </div>
                          </div>
                          );
                        })()
                      )}

                      {/* Mobile Carousel Controls - Overlay at bottom */}
                      {record.libraryItems &&
                        record.libraryItems.length > 1 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-1 py-0.5 flex items-center justify-between gap-1">
                            {/* Previous Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCarouselPrev(
                                  record.id,
                                  record.libraryItems!.length
                                );
                              }}
                              className="p-0.5 rounded bg-white/20 hover:bg-white/30 transition-colors"
                              aria-label="Previous"
                            >
                              <ArrowLeft className="w-2.5 h-2.5 text-white" />
                            </button>

                            {/* Indicator */}
                            <div className="text-[9px] text-white font-semibold">
                              {(carouselIndexes[record.id] || 0) + 1}/
                              {record.libraryItems.length}
                      </div>

                            {/* Next Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCarouselNext(
                                  record.id,
                                  record.libraryItems!.length
                                );
                              }}
                              className="p-0.5 rounded bg-white/20 hover:bg-white/30 transition-colors"
                              aria-label="Next"
                            >
                              <ArrowRight className="w-2.5 h-2.5 text-white" />
                            </button>
                    </div>
                        )}
                    </div>
                  </div>

                  {/* Right: Info Grid */}
                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    {/* Model Endpoint */}
                    <div>
                      {record.isClickable ? (
                        <button
                          onClick={() => handleModelClick(record)}
                          disabled={clickingRecordId === record.id}
                          className="text-sm font-medium text-text-tertiary dark:text-gray-300 underline hover:text-text-primary dark:hover:text-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left truncate block w-full"
                        >
                          {clickingRecordId === record.id
                            ? t("common.loading", "Loading") + "..."
                            : record.modelEndpoint}
                        </button>
                      ) : (
                        <p className="text-sm font-medium text-text-tertiary dark:text-gray-300 truncate">
                          {record.modelEndpoint}
                        </p>
                      )}
                    </div>

                    {/* Grid: 2 rows x 2 cols */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                      {/* Status */}
                      <div>
                        <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                          {t("settings.usage.status")}
                        </p>
                        <StatusBadge status={record.status} />
                      </div>

                      {/* Run Time */}
                      <div>
                        <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                          {t("settings.usage.runTime")}
                        </p>
                        <p className="text-xs font-medium text-text-primary dark:text-gray-100">
                          {record.runTime.toFixed(2)}s
                        </p>
                      </div>

                      {/* Token Amount */}
                      <div>
                        <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                          {t("settings.usage.tokenAmount")}
                        </p>
                        <p className="text-xs font-medium text-text-primary dark:text-gray-100">
                          {record.tokenAmount.toLocaleString()}
                        </p>
                      </div>

                      {/* Date */}
                      <div>
                        <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                        {t("settings.usage.date")}
                      </p>
                        <p className="text-[10px] text-text-tertiary dark:text-gray-300">
                        {record.formattedDate}
                      </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Mobile Pagination */}
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-3 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-xs font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={t("settings.usage.previous")}
              >
                <ChevronLeft className="w-3 h-3" />
                {!isMobile && <span>{t("settings.usage.previous")}</span>}
              </button>

              <div className="flex gap-1">
                {paginationItems.map((item: PaginationItem, index) => {
                  if (item === PAGINATION_DOTS) {
                    return (
                      <span
                        key={`mobile-dots-${index}`}
                        className="px-2 text-text-quaternary dark:text-gray-400"
                      >
                        ...
                      </span>
                    );
                  }

                  const page = item as number;

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-colors ${
                        currentPage === page
                          ? "bg-gray-50 dark:bg-gray-700 text-text-secondary dark:text-gray-200"
                          : "text-text-quaternary dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPagesCount}
                className="flex items-center gap-1 px-3 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-xs font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={t("settings.usage.next")}
              >
                {!isMobile && <span>{t("settings.usage.next")}</span>}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Image Viewer */}
      {selectedImage && (
        <ImageViewer
          image={selectedImage.url}
          onClose={() => setSelectedImage(null)}
          messageId={selectedImage.id}
        />
      )}

      {/* Video Viewer */}
      {selectedVideo && (
        <VideoViewer
          video={selectedVideo.url}
          onClose={() => setSelectedVideo(null)}
          messageId={selectedVideo.id}
          title={selectedVideo.title}
        />
      )}

      {/* Audio Viewer */}
      {selectedAudio && (
        <AudioViewer
          audio={selectedAudio.url}
          onClose={() => setSelectedAudio(null)}
          messageId={selectedAudio.id}
          title={selectedAudio.title}
        />
      )}
    </div>
  );
};
